import * as React from 'react';
import { Container, Form, Button } from 'semantic-ui-react';
import ReCAPTCHA from 'react-google-recaptcha';
import * as Sentry from '@sentry/browser';
import { recaptchaSize } from '../../helpers/common';
import { getBase64, handleLargeFilesIfRequired, submitFormData, validateInputSize, validateInputType } from '../../helpers/form-api';
import InputFile from '../InputFile';
import { StaticImage } from 'gatsby-plugin-image';

const recaptchaKey = process.env.GATSBY_GOOGLE_RECAPTCHA_KEY || '';

function checkDisplayOtherField(value) {
  return value === 'Other';
}

export default class VegetationManagementForm extends React.Component {
  initialFormData = {
    first_name: '',
    last_name: '',
    phone: '',
    email_address: '',
    council_area: '',
    nmi_number: '',
    street_address: '',
    billing_address: '',
    reason_for_enquiry: '',
    other_reason_for_enquiry: '',
    additional_comments: '',
  };

  state = {
    error: null,
    success: null,
    submitting: false,
    showOther: false,
    formData: { ...this.initialFormData },
    errors: {},
  };

  attachmentInputs = [React.createRef(), React.createRef(), React.createRef(), React.createRef(), React.createRef(), React.createRef()];
  attachmentInputWrappers = [
    React.createRef(),
    React.createRef(),
    React.createRef(),
    React.createRef(),
    React.createRef(),
    React.createRef(),
  ];
  recaptchaRef = React.createRef();

  handleCaptcha = (value) => {
    this.setState({ captcha: value });
  };

  handleInputChange = (event) => {
    const target = event.currentTarget;
    const value = event.target.type === 'checkbox' ? event.target.checked : target.value;
    const name = event.target.type === 'checkbox' ? event.target.name : target.name;

    const updatedFormData = {
      [name]: value,
    };

    switch (name) {
      case 'billing_address_same_as_site_address':
        if (value) {
          updatedFormData.billing_address = this.state.formData.street_address;
        } else {
          updatedFormData.billing_address = '';
        }
        break;
      case 'street_address':
        if (this.state.formData.billing_address_same_as_site_address) {
          updatedFormData.billing_address = value;
        }
        break;
    }

    this.setState((prevState) => ({
      ...prevState,
      formData: {
        ...prevState.formData,
        ...updatedFormData,
      },
    }));
  };

  handleSelectChange = (event, { name, value }) => {
    this.setState((prevState) => ({
      ...prevState,
      formData: {
        ...prevState.formData,
        [name]: value,
        other_reason_for_enquiry: checkDisplayOtherField(value) ? this.state.formData.other_reason_for_enquiry : '',
      },
      showOther: checkDisplayOtherField(value) ? true : false,
    }));
  };

  validateInputs = () => {
    let valid = true;
    this.setState((prevState) => ({
      ...prevState,
      errors: {
        ...prevState.errors,
      },
    }));

    if (this.state.formData.reason_for_enquiry === 'I have a tree that needs to be cut') {
      // Validate only the first 3 attachment inputs when isTreeCut is true
      this.attachmentInputs.slice(0, 3).forEach((ref, index) => {
        valid &= this.validateFile(ref, `attachmentInput${index + 1}`, true);
      });
    } else {
      // Validate all attachment inputs as optional
      this.attachmentInputs.forEach((ref, index) => {
        valid &= this.validateFile(ref, `attachmentInput${index + 1}`, false);
      });
    }

    return valid;
  };

  validateFile = (ref, errStateKey, required = false) => {
    let file,
      valid = true;

    if (ref.current) {
      file = ref.current.files[0];
      if (required && !file) {
        this.setState((prevState) => ({
          ...prevState,
          errors: {
            ...prevState.errors,
            [errStateKey]: 'Required',
          },
        }));
        valid = false;
      } else {
        this.setState((prevState) => ({
          ...prevState,
          errors: {
            ...prevState.errors,
            [errStateKey]: undefined,
          },
        }));
      }
    }

    return valid;
  };

  handleSubmit = async () => {
    try {
      this.setState({
        success: null,
        error: null,
        submitting: true,
      });

      const valid = this.validateInputs();
      if (!valid) {
        this.setState({
          error: 'Missing field.',
          success: null,
          submitting: false,
        });
        return;
      }

      if (this.state.captcha) {
        const attachments = {};
        const promises = [];
        this.attachmentInputs.forEach((input, index) => {
          const file = input.current?.files[0];
          if (file) {
            promises.push(
              (async () => {
                const key = `attachment${index + 1}`;
                attachments[key] = {
                  content: await getBase64(file),
                  name: file.name,
                };
              })()
            );
          }
        });
        await Promise.all(promises);

        const jobRequirements = [];
        if (this.state.formData.traffic_management_required) {
          jobRequirements.push('Traffic management required');
        }
        if (this.state.formData.clean_up_required) {
          jobRequirements.push('Clean up required');
        }

        const dataToPost = {
          ...this.state.formData,
          ...await handleLargeFilesIfRequired(attachments),
          billing_address_same_as_site_address: this.state.formData.billing_address_same_as_site_address ? 'Yes' : 'No',
          job_requirements: jobRequirements,
        };
        delete dataToPost.traffic_management_required;
        delete dataToPost.clean_up_required;

        const formData = {
          ...dataToPost,
          captchaResponse: this.state.captcha,
        };

        const response = await submitFormData('vegetation_management', formData);

        if (response.status !== 200) {
          this.setState({
            success: null,
            error: response.message,
            submitting: false,
            captcha: null,
          });
        } else {
          this.setState({
            success: 'Success',
            error: null,
            submitting: false,
            formData: { ...this.initialFormData },
            captcha: null,
          });
        }

        if (this.recaptchaRef.current) {
          this.recaptchaRef.current.reset();
        }
      } else {
        this.setState({
          success: null,
          error: 'Please tick ReCAPTCHA',
          submitting: false,
        });
      }
    } catch (err) {
      Sentry.captureException(err);
      this.setState({ error: 'Error', success: null, submitting: false });
    }
  };

  submitText = () => {
    if (this.state.submitting) {
      return 'Submitting...';
    } else if (this.state.success) {
      return 'Submitted';
    }
    return 'Submit';
  };

  render() {
    const nmiRequired = this.state.formData?.reason_for_enquiry === 'Vegetation No Go Zone' ? false : true;
    const isVegNoGoZone = this.state.formData?.reason_for_enquiry === 'Vegetation No Go Zone';
    const isTreeCut = this.state.formData?.reason_for_enquiry === 'I have a tree that needs to be cut';

    const uploadInfo = [
      'Please attach a photo of the number on the power pole that the wire from your house runs to.',
      'Please attach a photo from where the powerline attaches to your house, back towards the pole to show how close the wire and the tree are.',
      'Please upload a photo from the street towards your house, including the tree and the powerline.',
      'Please upload any other supporting photo that can show us the clearance between the tree and powerline.',
    ];

    const enquiryReason = [
      {
        text: 'Vegetation No Go Zone',
        value: 'Vegetation No Go Zone',
        key: 'vegetation-no-go-zone',
      },
      {
        text: 'I have a tree that needs to be cut',
        value: 'I have a tree that needs to be cut',
        key: 'I have a tree that needs to be cut',
      },
      {
        text: 'When are you coming to cut my tree?',
        value: 'When are you coming to cut my tree?',
        key: 'When are you coming to cut my tree?',
      },
      {
        text: 'When are you coming to collect the debris from trimming?',
        value: 'When are you coming to collect the debris from trimming?',
        key: 'When are you coming to collect the debris from trimming?',
      },
      {
        text: "I'm unhappy about how my trees were cut",
        value: "I'm unhappy about how my trees were cut",
        key: "I'm unhappy about how my trees were cut",
      },
      {
        text: 'Other',
        value: 'Other',
        key: 'Other',
      },
    ];

    const {
      first_name,
      last_name,
      phone,
      email_address,
      customer,
      council_area,
      nmi_number,
      street_address,
      billing_address,
      billing_address_same_as_site_address,
      lis_pole_number,
      job_type,
      reason_for_enquiry,
      other_reason_for_enquiry,
      additional_comments,
    } = this.state.formData;
    const {
      errors: { reason: reasonError, customer: customerError, jobType: jobTypeError },
    } = this.state;

    return (
      <section>
        <Container>
          <div className="grid-wrapper">
            <div className="form-container">
              <Form onSubmit={this.handleSubmit} data-testid="vegetation-management-enquiries-form">
                <h4>Vegetation management enquiry</h4>

                <Form.Select
                  label="Reason for enquiry"
                  required
                  options={enquiryReason}
                  placeholder="Please choose"
                  name="reason_for_enquiry"
                  value={reason_for_enquiry}
                  onChange={this.handleSelectChange}
                  id="form-reason-for-enquiry"
                  error={reasonError != null}
                />

                <Form.Input
                  label="First name"
                  required
                  name="first_name"
                  type="text"
                  value={first_name}
                  onChange={this.handleInputChange}
                  id="form-first-name"
                />
                <Form.Input
                  label="Last name"
                  required
                  name="last_name"
                  type="text"
                  value={last_name}
                  onChange={this.handleInputChange}
                  id="form-last-name"
                />
                <Form.Input
                  label="Phone number"
                  required
                  type="text"
                  name="phone"
                  value={phone}
                  onChange={this.handleInputChange}
                  id="form-phone"
                />
                <Form.Input
                  label="Email"
                  required
                  type="email"
                  name="email_address"
                  value={email_address}
                  onChange={this.handleInputChange}
                  id="form-email-address"
                />
                {!isVegNoGoZone && (
                  <Form.Input
                    label="NMI"
                    required={nmiRequired}
                    type="text"
                    name="nmi_number"
                    value={nmi_number}
                    onChange={this.handleInputChange}
                    id="form-nmi-number"
                  />
                )}

                {isVegNoGoZone && (
                  <>
                    <Form.Select
                      label="Customer"
                      required
                      options={[
                        { text: 'Council enquiry', value: 'Council enquiry' },
                        { text: 'Private enquiry', value: 'Private enquiry' },
                      ]}
                      name="customer"
                      value={customer}
                      onChange={this.handleSelectChange}
                      id="form-customer"
                      error={customerError != null}
                    />
                  </>
                )}

                <Form.Input
                  label="Council area"
                  required
                  type="text"
                  name="council_area"
                  value={council_area}
                  onChange={this.handleInputChange}
                  id="form-council-area"
                />
                <Form.Input
                  label="Site address (incl. suburb and postcode)"
                  required
                  type="text"
                  name="street_address"
                  value={street_address}
                  onChange={this.handleInputChange}
                  id="form-street-address"
                />

                {isVegNoGoZone && (
                  <>
                    <Form.Input
                      label="Billing address"
                      type="text"
                      name="billing_address"
                      value={billing_address}
                      onChange={this.handleInputChange}
                      id="form-billing-address"
                      disabled={billing_address_same_as_site_address}
                    />
                    <Form.Checkbox
                      checked={billing_address_same_as_site_address}
                      label="Same as site address"
                      name="billing_address_same_as_site_address"
                      onChange={this.handleInputChange}
                      id="form-billing-address-same-as-site-address"
                    />
                    {customer === 'Council enquiry' && (
                      <>
                        <Form.TextArea
                          label="LIS#/ pole number(s)"
                          required
                          name="lis_pole_number"
                          value={lis_pole_number}
                          onChange={this.handleInputChange}
                          id="form-lis-pole-number"
                        />
                        <Form.Select
                          label="Job type"
                          required
                          options={[
                            { text: 'Shutdown', value: 'Shutdown' },
                            {
                              text: 'Day rate (multiple live line spans)',
                              value: 'Day rate (multiple live line spans)',
                            },
                            {
                              text: 'Live line (individual span)',
                              value: 'Live line (individual span)',
                            },
                            {
                              text: 'Standard LV crew',
                              value: 'Standard LV crew',
                            },
                            { text: 'Unknown', value: 'Unknown' },
                          ]}
                          name="job_type"
                          value={job_type}
                          onChange={this.handleSelectChange}
                          id="form-job-type"
                          error={jobTypeError != null}
                        />
                        <div className="field">
                          <label htmlFor="job_requirements">Tick if required</label>
                        </div>
                        <Form.Checkbox
                          label="Traffic management required"
                          name="traffic_management_required"
                          onChange={this.handleInputChange}
                          id="form-traffic-management-required"
                        />
                        <Form.Checkbox
                          label="Clean up required"
                          name="clean_up_required"
                          onChange={this.handleInputChange}
                          id="form-clean-up-required"
                        />
                      </>
                    )}
                    <Form.TextArea
                      label="Additional comments"
                      name="additional_comments"
                      value={additional_comments}
                      onChange={this.handleInputChange}
                      id="form-additional-comments"
                    />
                    {this.attachmentInputs.map((attachmentInput, index) => (
                      <InputFile
                        key={index}
                        label={`Supporting document ${index + 1}`}
                        name={`file_${index + 1}`}
                        text="No file chosen"
                        restrictions="Max. filesize 5MB. Please upload only pdf, doc, docx, xls, xslx, pages, numbers, jpg, tiff, bmp, png, txt, csv, rtf, odt or ods files. Note that if form submission fails, please re-upload attachment."
                        id={`form-file-${index + 1}`}
                        maxFileSize={5}
                        refProp={attachmentInput}
                        ref={this.attachmentInputWrappers[index]}
                        error={this.state.errors[`attachmentInput${index + 1}`] != null}
                      />
                    ))}
                  </>
                )}

                {isTreeCut && (
                  <>
                    <div className="info-only field">
                      <h4>Supporting images</h4>
                      <p className="vegetation-management-form__example-label">
                        To make this request you will be required to submit some photos. We have provided examples and explanations below.
                        These will help us to understand what the next steps are.
                      </p>
                    </div>
                    <div className="vegetation-management-form__attachment-grid">
                      <div className="vegetation-management-form__image-zero vegetation-management-form__image">
                        <p className="vegetation-management-form__example-label">
                          <strong>Supporting image 1:</strong>
                        </p>
                        <StaticImage
                          src="../../assets/images/vegetation-management-form-images/image0.jpg"
                          quality={100}
                          formats={['jpg']}
                          alt="Check outages in your area"
                          objectFit="contain"
                        />
                      </div>
                      <div className="vegetation-management-form__image-one vegetation-management-form__image">
                        <p className="vegetation-management-form__example-label">
                          <strong>Supporting image 2:</strong>
                        </p>
                        <StaticImage
                          src={`../../assets/images/vegetation-management-form-images/image1.jpg`}
                          quality={100}
                          formats={['jpg']}
                          alt="Check outages in your area"
                          objectFit="contain"
                        />
                      </div>
                      <div className="vegetation-management-form__image-two vegetation-management-form__image">
                        <p className="vegetation-management-form__example-label">
                          <strong>Supporting image 3:</strong>
                        </p>
                        <StaticImage
                          src={`../../assets/images/vegetation-management-form-images/image2.jpg`}
                          quality={100}
                          formats={['jpg']}
                          alt="Check outages in your area"
                          objectFit="contain"
                        />
                      </div>
                      <div className="vegetation-management-form__image-three vegetation-management-form__image">
                        <p className="vegetation-management-form__example-label">
                          <strong>Supporting image 4:</strong>
                        </p>
                        <StaticImage
                          src={`../../assets/images/vegetation-management-form-images/image3.jpg`}
                          quality={100}
                          formats={['jpg']}
                          alt="Check outages in your area"
                          objectFit="contain"
                        />
                      </div>
                      {this.attachmentInputs.slice(0, 4).map((attachmentInput, index) => {
                        return (
                          <div
                            key={index}
                            className={`${index === 3 ? '' : 'required'} field vegetation-management-form-input vegetation-management-form__input${index}`}
                          >
                            <InputFile
                              key={index}
                              label={`Upload supporting image ${index + 1}`}
                              info={uploadInfo[index]}
                              name={`file_${index + 1}`}
                              text="No file chosen"
                              restrictions="Max. filesize 5MB. Please upload only jpg, jpeg or png files. Note that if form submission fails, please re-upload attachment."
                              id={`form-file-${index + 1}`}
                              maxFileSize={5}
                              accept={'.jpg, .jpeg, .png'}
                              refProp={attachmentInput}
                              ref={this.attachmentInputWrappers[index]}
                              error={this.state.errors[`attachmentInput${index + 1}`] != null}
                              required={index === 3 ? false : true}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {this.state.showOther && (
                  <Form.Input
                    label="Description of enquiry"
                    required
                    type="text"
                    name="other_reason_for_enquiry"
                    value={other_reason_for_enquiry}
                    onChange={this.handleInputChange}
                    id="form-other-reason-for-enquiry"
                  />
                )}

                <ReCAPTCHA ref={this.recaptchaRef} sitekey={recaptchaKey} onChange={this.handleCaptcha} size={recaptchaSize()} />

                <Button className="form--submit-btn" disabled={this.state.submitting} loading={this.state.submitting}>
                  {this.submitText()}
                </Button>

                {this.state.success ? <div className="success-message">{this.state.success}</div> : null}
                {this.state.error ? <div className="error-message">{this.state.error}</div> : null}
              </Form>
            </div>
          </div>
        </Container>
      </section>
    );
  }
}
